import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../core/contexts/ThemeContext';
import { useWindows } from '../../core/contexts/WindowContext';
export function HomeApp() {
  const {
    theme,
    t
  } = useTheme();
  const {
    openWindow
  } = useWindows();
  const isLightTheme = theme === 'light';
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 28
      }
    }
  };
  const skills = ['Product Design', 'UX Research', 'Frontend Dev', 'Websites & Apps', 'Design Systems', 'AI Tooling'];
  // Typing animation state
  const [displayedText, setDisplayedText] = useState('');
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const fullText = t('home.pitch');
  useEffect(() => {
    // Start typing after stagger animation delay
    const startDelay = setTimeout(() => {
      let currentIndex = 0;
      const typingInterval = setInterval(() => {
        if (currentIndex <= fullText.length) {
          setDisplayedText(fullText.slice(0, currentIndex));
          currentIndex++;
        } else {
          setIsTypingComplete(true);
          clearInterval(typingInterval);
        }
      }, 35); // 35ms per character
      return () => clearInterval(typingInterval);
    }, 800); // Wait 800ms for stagger animation
    return () => clearTimeout(startDelay);
  }, [fullText]);
  return <div className="h-full overflow-y-auto p-5 md:p-12 lg:p-16 pb-28" data-id="element-44">
      <div className="flex flex-col justify-center min-h-full" data-id="element-45">
        <motion.div className="max-w-5xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center" variants={containerVariants} initial="hidden" animate="visible" data-id="element-46">
          {/* Left: Content */}
          <motion.div variants={itemVariants} className="lg:col-span-8 space-y-8 order-2 lg:order-1" data-id="element-47">
            {/* Mobile Avatar */}
            <div className="block lg:hidden mb-2" data-id="element-48">
              <div className={`w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden border-2 shadow-lg ${isLightTheme ? 'border-white shadow-black/10' : 'border-[#2A2A2A] shadow-black/40'}`} data-id="element-49">
                <img src="https://cdn.magicpatterns.com/uploads/6LWFciUpqXzvc9S9PJFRtx/Scherm%C2%ADafbeelding_2026-04-19_om_14.10.57.png" alt="Mick van Latum" className="w-full h-full object-cover" data-id="element-50" />
              </div>
            </div>

            <div className="space-y-3 md:space-y-4" data-id="element-51">
              <h1 className={`text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] ${isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]'}`} data-id="element-52">
                Mick van Latum
              </h1>
              <p className="font-mono text-xs sm:text-sm uppercase tracking-widest text-[#C8956C]" data-id="element-53">
                {t('home.role')}
              </p>
            </div>

            <p className={`text-lg sm:text-xl md:text-2xl leading-relaxed ${isLightTheme ? 'text-[#6B6B6B]' : 'text-[#9B9B9B]'}`} data-id="element-54">
              {displayedText}
              {!isTypingComplete && <motion.span animate={{
              opacity: [1, 0]
            }} transition={{
              duration: 0.8,
              repeat: Infinity,
              repeatType: 'reverse'
            }} className={`inline-block w-[2px] h-[1.2em] ml-1 align-middle ${isLightTheme ? 'bg-[#1A1A1A]' : 'bg-[#E8E8E6]'}`} data-id="element-55" />}
            </p>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 pt-5" data-id="element-56">
              <button onClick={() => openWindow('work', t('work.title'))} className="px-6 py-3.5 rounded-xl font-medium text-sm transition-all bg-[#C8956C] text-white hover:bg-[#B5825A] shadow-lg shadow-[#C8956C]/20 text-center" data-id="element-57">
                {t('home.cta_work')}
              </button>
              <button onClick={() => openWindow('contact', t('contact.title'))} className={`px-6 py-3.5 rounded-xl font-medium text-sm border transition-all text-center ${isLightTheme ? 'border-black/[0.06] text-[#1A1A1A] hover:bg-black/5' : 'border-white/[0.06] text-[#E8E8E6] hover:bg-white/5'}`} data-id="element-58">
                {t('home.cta_contact')}
              </button>
            </div>

            <div className="flex items-center gap-2 pt-4" data-id="element-59">
              <div className="w-2 h-2 rounded-full bg-[#27C93F] animate-pulse" data-id="element-60" />
              <span className={`font-mono text-xs ${isLightTheme ? 'text-[#6B6B6B]' : 'text-[#9B9B9B]'}`} data-id="element-61">
                {t('home.available')}
              </span>
            </div>
          </motion.div>

          {/* Right: Photo */}
          <motion.div variants={itemVariants} className="hidden lg:block lg:col-span-4 order-1 lg:order-2" data-id="element-62">
            <div className={`relative aspect-square rounded-2xl overflow-hidden border shadow-xl ${isLightTheme ? 'border-black/[0.06] shadow-black/5' : 'border-white/[0.06] shadow-black/40'}`} data-id="element-63">
              <div className="absolute inset-0 bg-gradient-to-tr from-[#C8956C]/20 to-transparent mix-blend-overlay z-10" data-id="element-64" />
              <img src="https://cdn.magicpatterns.com/uploads/6LWFciUpqXzvc9S9PJFRtx/Scherm%C2%ADafbeelding_2026-04-19_om_14.10.57.png" alt="Mick van Latum" className="w-full h-full object-cover object-[20%_top]" data-id="element-65" />
            </div>
          </motion.div>
        </motion.div>

        {/* Bottom: Skills */}
        <motion.div variants={itemVariants} className={`mt-16 pt-10 border-t max-w-5xl mx-auto w-full ${isLightTheme ? 'border-black/[0.06]' : 'border-white/[0.06]'}`} data-id="element-66">
          <div className="flex flex-wrap gap-2.5 sm:gap-3" data-id="element-67">
            {skills.map((skill, i) => <motion.div key={skill} animate={{
            y: [0, -3, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.2
          }} whileHover={{
            scale: 1.05,
            y: -2,
            transition: {
              duration: 0.2
            }
          }} className={`px-5 py-2.5 rounded-lg font-mono text-xs border ${isLightTheme ? 'bg-white/80 backdrop-blur-sm border-black/[0.06] text-[#6B6B6B] hover:border-[#C8956C] hover:text-[#C8956C] hover:shadow-md' : 'bg-[#1A1A1A]/80 backdrop-blur-sm border-white/[0.06] text-[#9B9B9B] hover:border-[#C8956C] hover:text-[#C8956C] hover:shadow-[0_4px_12px_rgba(200,149,108,0.15)]'} transition-all cursor-default`} data-id="element-68">
                {skill}
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </div>;
}